import { apiClient } from './api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3101/api';

/** Formats the server accepts. Mirrors ALLOWED_FORMATS in the API. */
export const ACCEPTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

/** Mirrors MAX_BYTES in the API. Client-side checks are courtesy; the server decides. */
export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;

export interface UploadedAsset {
  id: string;
  /** API-relative path, e.g. "/assets/abc123". */
  url: string;
  mimeType: string;
  width: number;
  height: number;
  byteSize: number;
}

/**
 * URL for an asset, as saved into flow content. Points at our redirect endpoint
 * rather than at storage: presigned URLs expire in minutes, so persisting one
 * would produce images that break on reload.
 *
 * Deployed builds set VITE_API_URL to the relative `/api`, so this is
 * `/api/assets/<id>` and resolves against whichever host serves the flow. Older
 * content embeds the absolute `https://portal.hollowayhq.io/api/...`, which ties
 * those images to that hostname; relative paths don't, so new content survives a
 * change of domain or a recovery onto a different one.
 */
export function assetUrl(assetId: string): string {
  return `${API_URL}/assets/${assetId}`;
}

/**
 * Fast local rejection so the user isn't waiting on a round trip to learn the
 * file is wrong. The server repeats every one of these checks against the
 * actual bytes — this only reads the metadata the browser reports.
 */
export function precheckImage(
  file: File,
  limits?: { maxBytes?: number; acceptedMimeTypes?: string[] },
): string | null {
  const maxBytes = Math.min(limits?.maxBytes ?? MAX_IMAGE_BYTES, MAX_IMAGE_BYTES);
  const accepted = limits?.acceptedMimeTypes ?? ACCEPTED_IMAGE_MIME_TYPES;

  if (file.size === 0) return 'That file is empty.';
  if (file.size > maxBytes) {
    return `Image must be under ${(maxBytes / (1024 * 1024)).toFixed(0)}MB.`;
  }
  if (!accepted.includes(file.type)) {
    return 'Please choose a JPEG, PNG, WebP, or GIF image.';
  }
  return null;
}

/** Pull a displayable message out of an axios error, falling back sensibly. */
export function uploadErrorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { message?: string | string[] } } })
    ?.response;
  const message = response?.data?.message;
  if (Array.isArray(message)) return message[0];
  if (typeof message === 'string') return message;
  return 'Upload failed. Please try again.';
}

export const assetsApi = {
  /** Authenticated upload of an image destined for flow content. */
  async uploadFlowContent(file: File, flowId?: string): Promise<UploadedAsset> {
    const form = new FormData();
    form.append('file', file);
    if (flowId) form.append('flowId', flowId);

    const { data } = await apiClient.post<UploadedAsset>('/assets', form, {
      // Let the browser set the multipart boundary; the client's JSON default
      // would otherwise override it and the server would reject the body.
      headers: { 'Content-Type': undefined },
    });
    return data;
  },

  /**
   * Anonymous upload from an ImageUpload element during a live flow run. The
   * session id is the capability, so no auth header is needed or sent.
   */
  async uploadSessionAsset(
    sessionId: string,
    file: File,
    limits?: { maxSizeMB?: number; acceptedFormats?: string[] },
  ): Promise<UploadedAsset> {
    const form = new FormData();
    form.append('file', file);
    if (limits?.maxSizeMB) form.append('maxSizeMB', String(limits.maxSizeMB));
    if (limits?.acceptedFormats?.length) {
      form.append('acceptedFormats', limits.acceptedFormats.join(','));
    }

    const { data } = await apiClient.post<UploadedAsset>(
      `/run/session/${sessionId}/assets`,
      form,
      { headers: { 'Content-Type': undefined } },
    );
    return data;
  },
};
